import { useEffect, useLayoutEffect, useRef } from 'react';
import { useToggle } from 'react-use';
import { useOverflowX } from 'src/hooks/useOverflowX';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature } from 'src/WebApiController';

const HorizontalWidthBuffer = 5; // 5px

/***
 * Custom hook to check if we want to use horizontal/vertical view of the undercut price settings.
 */
export const usePriceViewDirection = () => {
  const hasDynamicUnderCutSettingsInOnePagerFeature = useUserHasFeature(
    Feature.DynamicUnderCutSettingsInOnePager
  );
  const { ref, isOverflowX } = useOverflowX();
  // Max horizontal width, records the max width that won't trigger the overflow
  // will be used to switch back to horizontal view by checking the current client width
  const maxHorizontalWidth = useRef<number>();
  const [useVerticalView, setUseVerticalView] = useToggle(false);

  useEffect(() => {
    if (isOverflowX && ref.current) {
      if (
        !maxHorizontalWidth.current ||
        ref.current.clientWidth > maxHorizontalWidth.current
      ) {
        maxHorizontalWidth.current = ref.current.clientWidth;
      }
      setUseVerticalView(true);
    }
  }, [isOverflowX, ref, setUseVerticalView]);

  useLayoutEffect(() => {
    const { current } = ref;

    const resizeObserver = new ResizeObserver(() => {
      if (current && maxHorizontalWidth.current) {
        if (
          current.clientWidth >
            maxHorizontalWidth.current + HorizontalWidthBuffer &&
          useVerticalView
        ) {
          setUseVerticalView(false);
        }
      }
    });

    if (current) {
      resizeObserver.observe(current);
    }

    return () => resizeObserver.disconnect();
  }, [ref, maxHorizontalWidth, useVerticalView, setUseVerticalView]);

  return {
    ref,
    shouldUseVerticalView:
      useVerticalView && hasDynamicUnderCutSettingsInOnePagerFeature,
  };
};
